// src/contexts/LanguageProvider.tsx
// ★ Language context provider — stores active language in localStorage
// and exposes { language, setLanguage, t(key) } for i18n.

import React, { createContext, useState, useCallback, useMemo, useEffect } from 'react';
import type { Language } from '../config/i18n';
import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES } from '../config/i18n';
import { uiStrings } from '../content/ui-strings';

export interface LanguageContextValue {
    /** Active language code */
    language: Language;
    /** Switch to a different language (persists to localStorage) */
    setLanguage: (lang: Language) => void;
    /** Translate a UI string key */
    t: (key: string) => string;
}

export const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

function getInitialLanguage(): Language {
    try {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
        if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
    } catch {
        // localStorage unavailable (SSR, privacy mode)
    }
    return DEFAULT_LANGUAGE;
}

interface LanguageProviderProps {
    children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(getInitialLanguage);

    const setLanguage = useCallback((lang: Language) => {
        setLanguageState(lang);
        try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
        } catch {
            // ignore
        }
    }, []);

    // Set <html lang="..."> attribute when language changes
    useEffect(() => {
        document.documentElement.setAttribute('lang', language);
    }, [language]);

    const t = useCallback(
        (key: string): string => {
            const strings = uiStrings[language];
            return strings?.[key] ?? uiStrings[DEFAULT_LANGUAGE]?.[key] ?? key;
        },
        [language]
    );

    const value = useMemo(
        () => ({ language, setLanguage, t }),
        [language, setLanguage, t]
    );

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};
